import moment from 'moment';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { GetDisplayNameForFieldType, Keys } from '../helpers/constants';
import { compareArrays } from '../helpers/utility';
import { PositionBase, TemplateCoordinates } from '../types/template-types';
import { getFriendlyNameForType, getIn } from './../helpers/helpers';
import { getDataLayoutPositionKeyPathsByFieldId, getFieldKeyPathById, getFieldPositionKeyPathsByFieldId } from './selectors';
import { FieldFeatures, KeyPath, TemplateState, TemplateViewIndex } from './templateeditor-types';
import { getDateTimeFormat } from '../helpers/DateTimeUtility';
import { Features, getFeature } from '../helpers/PermissionUtility';

export const adjustRowHeightsByOffset = (rowHeights: number[], offsetHeight: number, resizeProportially: boolean) => {
  const minRowHeight = 4;
  const newRowHeights = [...rowHeights];
  let remainingOffsetHeight = offsetHeight;

  if (!resizeProportially) {
    const newLastRowHeight = newRowHeights[newRowHeights.length - 1] + offsetHeight;
    const realLastRowHeight = Math.max(newLastRowHeight, minRowHeight);

    remainingOffsetHeight = newLastRowHeight - realLastRowHeight;
    newRowHeights[newRowHeights.length - 1] = realLastRowHeight;

    // 0 or NaN, return?
    if (!remainingOffsetHeight) {
      return newRowHeights;
    }
  }

  const offsetPerRow = remainingOffsetHeight / rowHeights.length;
  const roundedOffsetPerRow = Math.round(offsetPerRow);
  const roundingDifference = offsetPerRow - roundedOffsetPerRow;
  const rowAdjustInterval = Math.abs(Math.round(1 / roundingDifference));
  const rowMakeBigger = roundingDifference > 0.0;

  for (let rowNumber = 0; rowNumber < rowHeights.length; rowNumber++) {
    let rowHeight = rowHeights[rowNumber] + roundedOffsetPerRow;

    if (rowAdjustInterval > 1 && (rowNumber + 1) % rowAdjustInterval === 0) {
      if (rowMakeBigger) {
        rowHeight++;
      } else {
        rowHeight--;
      }
    }
    newRowHeights[rowNumber] = Math.max(rowHeight, minRowHeight);
  }

  return newRowHeights;
};

export const getMinRowHeight = (rowHeights: number[]): number | null => {
  if (rowHeights.length === 0) {
    return null;
  }

  const minRowHeight = rowHeights.reduce((prev, current) => Math.min(prev, current));

  return minRowHeight;
};

export const isDuplicateFieldName = (state: TemplateState, fieldName: string): boolean => {
  const dup = <T extends { name: string }>({ name }: T) => name.toLowerCase() === fieldName.toLowerCase();

  if (state.template.fields.some(dup)) {
    return true;
  }

  return false;
};

export const getDefaultName = (state: TemplateState, type: number): string => {
  const typeName = getFriendlyNameForType(type);
  let fieldNumber = state.template.fields.length + 1;
  let fieldName = `${typeName} ${fieldNumber}`;
  let uniqueName = false;

  while (!uniqueName) {
    if (isDuplicateFieldName(state, fieldName)) {
      fieldNumber++;
      fieldName = `${typeName} ${fieldNumber}`;
    } else {
      uniqueName = true;
    }
  }
  return fieldName;
};

export const trackFirstFieldDropped = (fieldType: number, templateView: TemplateViewIndex) => {
  const type = GetDisplayNameForFieldType(fieldType);
  const view = templateView === 0 ? 'Form' : 'List';

  analyticsTrack('First Field Added', { type, view });
};

export const getSelectedFieldPositionsKeyPaths = (state: TemplateState): KeyPath[] => {
  // get keypaths from field instances in the 'list view'
  let keyPaths: KeyPath[] = state.selection?.dataLayoutPositions;

  if (!keyPaths) {
    // get keypaths from field instances in the 'section' of a 'list view'
    keyPaths = state.selection?.dataLayoutSections as KeyPath[];
  }

  if (!keyPaths) {
    // get keypaths from field instances in the 'form view'
    keyPaths = state.selection?.fieldPositions;
  }

  if (!keyPaths) {
    // if there are no selections, return an empty array
    keyPaths = [];
  }

  return keyPaths;
};

export const getFieldKeyPathFromSelectedField = (state: TemplateState): KeyPath[] => {
  // get all keypaths for the selected template fields
  const selectedFieldIds = state.selection.fieldIds;
  const keyPaths: KeyPath[] = [];

  if (!selectedFieldIds || selectedFieldIds.length === 0) {
    return keyPaths;
  }

  const templateFields = state.template.fields;

  for (const fieldId of selectedFieldIds) {
    // get keypath to the template field
    const fieldIndex = templateFields.findIndex((x) => x.id === fieldId);
    keyPaths.push(['template', 'fields', fieldIndex]);
  }

  return keyPaths;
};

export const getFieldPositionKeyPathsFromSelection = (state: TemplateState): KeyPath[] => {
  // get all keypaths for the selected template fields
  const selectedFieldIds = state.selection.fieldIds;
  const keyPaths: KeyPath[] = [];

  if (!selectedFieldIds || selectedFieldIds.length === 0) {
    return keyPaths;
  }

  for (const fieldId of selectedFieldIds) {
    // get all fieldPositions (i.e. field instances on form view) that link to this field
    const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);
    for (const keyPath of fieldPositionKeyPaths) {
      keyPaths.push(keyPath);
    }

    // get all dataLayoutPositions (i.e. field instances on list view) that link to this field
    const dataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, fieldId);
    for (const keyPath of dataLayoutPositionKeyPaths) {
      keyPaths.push(keyPath);
    }
  }

  return keyPaths;
};

export const getKeyPathsForFieldPositions = (state: TemplateState): KeyPath[] => {
  // get all keypaths for the selected template fields
  const selectedFieldIds = state.selection.fieldIds;
  const keyPaths: KeyPath[] = [];

  if (!selectedFieldIds || selectedFieldIds.length === 0) {
    return keyPaths;
  }

  for (const fieldId of selectedFieldIds) {
    // get all fieldPositions (i.e. field instances on form view) that link to this field
    const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);
    for (const keyPath of fieldPositionKeyPaths) {
      keyPaths.push(keyPath);
    }

    // get all dataLayoutPositions (i.e. field instances on list view) that link to this field
    const dataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, fieldId);
    for (const keyPath of dataLayoutPositionKeyPaths) {
      keyPaths.push(keyPath);
    }
  }

  return keyPaths;
};

export const getSelectedPositionFieldIds = (state: TemplateState): string[] => {
  // get all selected field instance Ids for form view
  const selectedPositionFieldIds = [];
  if (state.selection.fieldPositions && state.selection.fieldPositions.length > 0) {
    for (const keyPath of state.selection.fieldPositions) {
      const position = getIn<PositionBase>(state, keyPath);
      if (selectedPositionFieldIds.indexOf(position.fieldId) == -1) {
        selectedPositionFieldIds.push(position.fieldId);
      }
    }
  }

  // get all selected field instance Ids for list view
  if (state.selection.dataLayoutPositions && state.selection.dataLayoutPositions.length > 0) {
    for (const keyPath of state.selection.dataLayoutPositions) {
      const position = getIn<PositionBase>(state, keyPath);
      if (selectedPositionFieldIds.indexOf(position.fieldId) == -1) {
        selectedPositionFieldIds.push(position.fieldId);
      }
    }
  }

  return selectedPositionFieldIds;
};

export const getUnmappedFieldsFromSelection = (state: TemplateState, selectedPositionFieldIds: string[]): string[] => {
  // of the selected fields, find the ones that are not mapped to any field positions or data layout positions
  const unmappedFields = [];
  for (const fieldId of selectedPositionFieldIds) {
    const fieldPositionKeyPaths = state.selection.fieldPositions
      ? getFieldPositionKeyPathsByFieldId(state, fieldId).filter(
          (keyPath) => state.selection.fieldPositions.findIndex((fieldPosition) => compareArrays(keyPath, fieldPosition)) === -1
        )
      : getFieldPositionKeyPathsByFieldId(state, fieldId);
    const dataLayoutPositionKeyPaths = state.selection.dataLayoutPositions
      ? getDataLayoutPositionKeyPathsByFieldId(state, fieldId).filter(
          (keyPath) => state.selection.dataLayoutPositions.findIndex((fieldPosition) => compareArrays(keyPath, fieldPosition)) === -1
        )
      : getDataLayoutPositionKeyPathsByFieldId(state, fieldId);

    if (fieldPositionKeyPaths.length == 0 && dataLayoutPositionKeyPaths.length == 0) {
      unmappedFields.push(fieldId);
    }
  }

  return unmappedFields;
};

export const getFieldKeyPathsFromFieldIds = (state: TemplateState, fieldIds: string[]): KeyPath[] => {
  const fieldKeyPaths = [];
  for (const fieldId of fieldIds) {
    const fieldKeyPath = getFieldKeyPathById(state, fieldId) as KeyPath;
    if (!!fieldKeyPath) {
      fieldKeyPaths.push(fieldKeyPath);
    }
  }

  return fieldKeyPaths;
};

export const restoreFieldSelection = (state: TemplateState, selectedFieldIds: string[]): TemplateState => {
  if (!state.selection) {
    state.selection = {
      fieldIds: selectedFieldIds
    };
  } else {
    state.selection.fieldIds = selectedFieldIds;
  }

  return state;
};

export const getStatus = (isDirty: boolean, unpublishedChanges: boolean, lastSaveDate: string, lastUpdatedDate: string) => {
  if (isDirty) {
    return 'You have unsaved changes';
  }

  const status = `${unpublishedChanges ? 'Draft saved at' : 'Published at'} ${
    lastSaveDate ? lastSaveDate : moment.utc(lastUpdatedDate).local().format(getDateTimeFormat())
  }`;

  return status;
};

export const getOffset = (arrowKey: string, distance: number): TemplateCoordinates => {
  const offset: TemplateCoordinates = {};

  switch (arrowKey) {
    case Keys.ArrowLeft:
      offset.x = -distance;
      break;
    case Keys.ArrowUp:
      offset.y = -distance;
      break;
    case Keys.ArrowRight:
      offset.x = distance;
      break;
    case Keys.ArrowDown:
      offset.y = distance;
      break;
    default:
      break;
  }

  return offset;
};

export function getParentKeyPath(keyPath: KeyPath) {
  const parentKeyPath: KeyPath = [];

  for (let i = 0; i < keyPath.length - 1; i++) {
    parentKeyPath.push(keyPath[i]);
  }

  return parentKeyPath;
}

export function getParentKeyPaths(keyPaths: KeyPath[]) {
  const parentKeyPaths: KeyPath[] = [];

  for (const keyPath of keyPaths) {
    parentKeyPaths.push(getParentKeyPath(keyPath));
  }

  return parentKeyPaths;
}

export function getParentKeyPathByLevel(keyPath: KeyPath, levels = 1) {
  if (levels >= keyPath.length) {
    return [];
  }

  const parentKeyPath: KeyPath = [];

  for (let i = 0; i < keyPath.length - levels; i++) {
    parentKeyPath.push(keyPath[i]);
  }

  return parentKeyPath;
}

export function getFieldFeatures(): FieldFeatures {
  return {
    maxFileSize: getFeature(Features.FileAttachmentMaxFileSize),
    maxFileCount: getFeature(Features.FileAttachmentMaxFileCount),
    audioMaxLength: getFeature(Features.AudioFieldMaxLength) || 10
  };
}
